import {
  type CreateQuerySourceRequest,
  type QuerySourceDetail,
  QuerySourceDetailSchema,
  type QuerySourceListQuery,
  type QuerySourceListResponse,
  QuerySourceListResponseSchema,
  type QuerySourceOption,
  QuerySourceOptionListSchema,
  type QuerySourceOptionsQuery,
  type UpdateQuerySourceRequest,
} from '@utazzo/shared';

import { apiClient } from '@/lib/api-client';

/**
 * Query Source master API. Every response is validated with the shared Zod
 * schema so a server change can't sneak past the UI types.
 */

export const listQuerySources = async (
  query: QuerySourceListQuery,
): Promise<QuerySourceListResponse> => {
  const { data } = await apiClient.get<unknown>('/query-sources', { params: query });
  return QuerySourceListResponseSchema.parse(data);
};

export const getQuerySourceOptions = async (
  query?: QuerySourceOptionsQuery,
): Promise<QuerySourceOption[]> => {
  const { data } = await apiClient.get<unknown>('/query-sources/options', { params: query });
  return QuerySourceOptionListSchema.parse(data);
};

export const getQuerySource = async (id: string): Promise<QuerySourceDetail> => {
  const { data } = await apiClient.get<unknown>(`/query-sources/${id}`);
  return QuerySourceDetailSchema.parse(data);
};

export const createQuerySource = async (
  payload: CreateQuerySourceRequest,
): Promise<QuerySourceDetail> => {
  const { data } = await apiClient.post<unknown>('/query-sources', payload);
  return QuerySourceDetailSchema.parse(data);
};

export const updateQuerySource = async (
  id: string,
  payload: UpdateQuerySourceRequest,
): Promise<QuerySourceDetail> => {
  const { data } = await apiClient.patch<unknown>(`/query-sources/${id}`, payload);
  return QuerySourceDetailSchema.parse(data);
};

export const deleteQuerySource = async (id: string): Promise<void> => {
  await apiClient.delete(`/query-sources/${id}`);
};

export const bulkDeleteQuerySources = async (
  ids: string[],
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const { data } = await apiClient.post<{
    updated: number;
    failed: { id: string; error: string }[];
  }>('/query-sources/bulk-delete', { ids });
  return data;
};

export const bulkSetQuerySourcesStatus = async (
  ids: string[],
  isActive: boolean,
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const { data } = await apiClient.post<{
    updated: number;
    failed: { id: string; error: string }[];
  }>('/query-sources/bulk-set-status', { ids, isActive });
  return data;
};
